import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Alert, AlertStatus } from "../types/alert";
import {
  getUserAlerts,
  getFamilyAlerts,
  updateAlert,
} from "../services/alerts";
import { useAuth } from "./AuthContext";

interface AlertsContextType {
  alerts: Alert[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refreshAlerts: () => Promise<void>;
  markAsRead: (alertId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismissAlert: (alertId: string) => Promise<void>;
}

const AlertsContext = createContext<AlertsContextType | undefined>(undefined);

export const useAlerts = () => {
  const context = useContext(AlertsContext);
  if (context === undefined) {
    throw new Error("useAlerts must be used within an AlertsProvider");
  }
  return context;
};

interface AlertsProviderProps {
  children: React.ReactNode;
}

export const AlertsProvider: React.FC<AlertsProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate unread count
  const unreadCount = alerts.filter(
    (alert) => alert.status === AlertStatus.UNREAD
  ).length;

  // Fetch alerts for the current user
  const refreshAlerts = useCallback(async () => {
    if (!user) {
      setAlerts([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get both user-specific alerts and family-wide alerts
      const [userAlerts, familyAlerts] = await Promise.all([
        getUserAlerts(user.userId),
        getFamilyAlerts(user.familyId),
      ]);

      // Combine and deduplicate alerts
      const allAlerts = [...userAlerts, ...familyAlerts];
      const uniqueAlerts = allAlerts.filter(
        (alert, index, self) =>
          index === self.findIndex((a) => a.alertId === alert.alertId)
      );

      // Sort by priority and date (most recent first)
      const sortedAlerts = uniqueAlerts.sort((a, b) => {
        const priorityOrder = { URGENT: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
        const priorityDiff =
          priorityOrder[b.priority] - priorityOrder[a.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });

      setAlerts(sortedAlerts);
    } catch (err) {
      console.error("Error fetching alerts:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch alerts");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Mark a single alert as read
  const markAsRead = useCallback(async (alertId: string) => {
    try {
      await updateAlert(alertId, { status: AlertStatus.READ });
      setAlerts((prevAlerts) =>
        prevAlerts.map((alert) =>
          alert.alertId === alertId
            ? {
                ...alert,
                status: AlertStatus.READ,
                updatedAt: new Date().toISOString(),
              }
            : alert
        )
      );
    } catch (err) {
      console.error("Error marking alert as read:", err);
      throw err;
    }
  }, []);

  // Mark all alerts as read
  const markAllAsRead = useCallback(async () => {
    const unreadAlerts = alerts.filter(
      (alert) => alert.status === AlertStatus.UNREAD
    );

    try {
      // Update all unread alerts
      await Promise.all(
        unreadAlerts.map((alert) =>
          updateAlert(alert.alertId, { status: AlertStatus.READ })
        )
      );

      setAlerts((prevAlerts) =>
        prevAlerts.map((alert) =>
          alert.status === AlertStatus.UNREAD
            ? {
                ...alert,
                status: AlertStatus.READ,
                updatedAt: new Date().toISOString(),
              }
            : alert
        )
      );
    } catch (err) {
      console.error("Error marking all alerts as read:", err);
      throw err;
    }
  }, [alerts]);

  // Dismiss an alert (mark as dismissed)
  const dismissAlert = useCallback(async (alertId: string) => {
    try {
      await updateAlert(alertId, { status: AlertStatus.DISMISSED });
      setAlerts((prevAlerts) =>
        prevAlerts.filter((alert) => alert.alertId !== alertId)
      );
    } catch (err) {
      console.error("Error dismissing alert:", err);
      throw err;
    }
  }, []);

  // Fetch alerts when user changes
  useEffect(() => {
    refreshAlerts();
  }, [refreshAlerts]);

  // Poll for new alerts every 30 seconds
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      refreshAlerts();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [user, refreshAlerts]);

  const contextValue: AlertsContextType = {
    alerts,
    unreadCount,
    loading,
    error,
    refreshAlerts,
    markAsRead,
    markAllAsRead,
    dismissAlert,
  };

  return (
    <AlertsContext.Provider value={contextValue}>
      {children}
    </AlertsContext.Provider>
  );
};

// Custom hook for easy access to alerts functionality
export const useAlertsActions = () => {
  const { refreshAlerts, markAsRead, markAllAsRead, dismissAlert } =
    useAlerts();
  return { refreshAlerts, markAsRead, markAllAsRead, dismissAlert };
};

// Custom hook for alerts data
export const useAlertsData = () => {
  const { alerts, unreadCount, loading, error } = useAlerts();
  return { alerts, unreadCount, loading, error };
};
